import React, { useMemo } from "react";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import VerticalStepper from "../stepper";
import OfficerRegistration from "./OfficersRegistration";
import useOfficersForm from "./useOfficersForm";
import "./officers-form.scss";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../Layout";
import ReactCountryFlag from "react-country-flag";
import { getName } from "country-list";
import { Button } from "primereact/button";
import PropTypes from "prop-types";
import AddAttachments from "./AddAttachments";
import { Skeleton } from "primereact/skeleton";

const OfficersForm = ({
  setShowDialog,
  type,
  regData,
  setRegData,
  resetRegData,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    handleBack,
    setActiveIndex,
    activeIndex,
    entityId,
    btnToggle,
    setBtnToggle,
    formDetails,
    setFormDetails,
    isCongrats,
    setIsCongrats,
    isLoading,
    entity,
    entityLoading,
    handleOfficerSubmit,
    files,
    setFiles,
    officerConfig,
  } = useOfficersForm({
    type,
    setShowDialog,
    regData,
    setRegData,
    resetRegData,
    isNewOfficer: true,
  });

  const steps = [
    {
      label: type === "officer" ? "Officer Details" : "Attorney Details",
      content: (
        <OfficerRegistration
          btnToggle={btnToggle}
          setBtnToggle={setBtnToggle}
          type={type}
          formDetails={formDetails}
          setFormDetails={setFormDetails}
          regData={regData}
          setRegData={setRegData}
          isEditing={true}
          isNewOfficer={true}
        />
      ),
    },
    {
      label: "Attachments",
      content: (
        <AddAttachments
          isCongrats={isCongrats}
          setIsCongrats={setIsCongrats}
          files={files}
          setFiles={setFiles}
          entityData={entity}
          type={type}
          setActiveIndex={setActiveIndex}
          resetRegData={resetRegData}
          regData={regData}
        />
      ),
    },
  ];

  const handleNext = () => {
    setActiveIndex((prevActiveStep) =>
      Math.min(prevActiveStep + 1, steps.length - 1),
    );
  };

  const breadcrumbs = useMemo(
    () => [
      {
        label: <span className="breadcrumb-link">My Entities</span>,
        command: () => navigate("/entities"),
        id: "entities",
      },
      {
        label: (
          <div className="breadcrumb-link flex">
            {!entityLoading ? (
              <>
                {entity?.name} (
                <ReactCountryFlag
                  countryCode={entity?.country}
                  svg
                  className=""
                />{" "}
                {getName(entity?.country || "")})
              </>
            ) : (
              <>
                <Skeleton width="100px" height="20px" className="mr-1" />
                <Skeleton width="150px" height="20px" className="mr-1" />
              </>
            )}
          </div>
        ),
        id: "entity",
        command: () => navigate(`/entity/${id}/dashboard`),
      },
      {
        label: (
          <span className="breadcrumb-link">
            {officerConfig?.breadcrumbType}
          </span>
        ),
        command: () =>
          navigate(
            type === "officer"
              ? `/entity/${id}/appointments-ownership?tab=officers`
              : `/entity/${id}/appointments-ownership?tab=power-of-attorney`,
          ),
        id: "officer-data",
      },
      {
        label: officerConfig?.pageTitle,
        id: "add-new-officer",
      },
    ],
    [entity, entityLoading, id, navigate],
  );

  return (
    <Layout crumbs={breadcrumbs}>
      <div className="grid officer-form">
        <div className="lg:col-3 md:col-12 sm:col-12 xs:col-12 pt-4">
          <Card className="card-onboard">
            <>
              <p className="add-new-entity-heading">
                {officerConfig?.pageTitle}
              </p>
              <p className="input-label m-0">
                {officerConfig?.sidebarDescription}
              </p>
            </>
            <Divider />
            <div className="step-menu">
              <VerticalStepper options={steps} activeIndex={activeIndex} />
            </div>
          </Card>
        </div>
        <div className="lg:col-9 md:col-12 sm:col-12 xs:col-12 pt-4">
          <Card className="card-onboard">{steps[activeIndex].content}</Card>
          {!isCongrats ? (
            <div className="next-btn-footer">
              <div className="d-flex justify-content-start">
                <Button
                  className="generic-cancel"
                  onClick={() => {
                    navigate(
                      type === "officer"
                        ? `/entity/${id}/appointments-ownership?tab=officers`
                        : `/entity/${id}/appointments-ownership?tab=power-of-attorney`,
                    );
                  }}
                >
                  Cancel
                </Button>
              </div>
              <div className="flex justify-content-end gap-2">
                {activeIndex !== 0 && files.length === 0 ? (
                  <Button
                    onClick={handleBack}
                    disabled={activeIndex === 0}
                    className="cancel-btns"
                  >
                    Back
                  </Button>
                ) : (
                  ""
                )}
                {activeIndex === steps.length - 1 ? (
                  <Button
                    type="submit"
                    className={`add-new-contact ${files.length > 0 && "confirm-appointment"}`}
                    onClick={handleOfficerSubmit}
                    loading={isLoading}
                  >
                    {files.length > 0 ? "Complete" : "Skip"}
                  </Button>
                ) : (
                  <Button
                    disabled={btnToggle}
                    className={`add-new-contact ${btnToggle ? "disable" : null}`}
                    onClick={handleNext}
                  >
                    Next
                  </Button>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </Layout>
  );
};

OfficersForm.propTypes = {
  setShowDialog: PropTypes.func,
  type: PropTypes.string.isRequired,
  regData: PropTypes.object,
  setRegData: PropTypes.func,
  resetRegData: PropTypes.func,
};

export default OfficersForm;
